import { z } from "zod";
import { ACTOR_TYPE, EVENT_SUBJECT_TYPE } from "../../constants/eventLog.js";
import { decimalString, entityId } from "../common.js";
/**
 * The parts of an `EventLogs` row that are the same whatever feature wrote it.
 *
 * Feature schemas extend these rather than restating them, so a change to the table's
 * shape lands in one place instead of once per feature.
 */
/**
 * A charge row as it stood before the operation ran.
 *
 * This is a full before-image: `preChange` answers "what was there", so a dispute can be
 * reconstructed without joining back to rows that may since have been deleted.
 */
export const chargeBeforeSchema = z
    .object({
    chargeId: entityId,
    amount: decimalString,
    quantity: z.number().nullable().optional(),
})
    .strict();
/** A charge created by an operation, paired with the charge it was derived from. */
export const chargeCreatedSchema = z
    .object({
    newChargeId: entityId,
    /** The charge this one was calculated from, via `chargeBasedId`. */
    originalChargeId: entityId.nullable(),
    amount: decimalString,
    quantity: z.number().nullable().optional(),
})
    .strict();
/** A charge put back after an earlier operation was undone. */
export const chargeRestoredSchema = z
    .object({
    newChargeId: entityId,
    restoredFromChargeId: entityId.nullable(),
    amount: decimalString,
})
    .strict();
/**
 * All relevant values *before* the operation - a complete before-image.
 *
 * Asymmetric with `postChange` by design (see below). Everything is optional because not
 * every event has a meaningful prior state to capture; an event that changed no charges
 * still records the booking status it started from.
 */
export const preChangeSchema = z
    .object({
    bookingStatus: z.string().nullable().optional(),
    charges: z.array(chargeBeforeSchema).optional(),
    /** Summed from `charges`, so a reader does not have to re-add them. */
    chargesTotal: decimalString.optional(),
})
    .strict();
/**
 * Only what actually changed - a sparse delta, not a full after-image.
 *
 * Reading "what did it look like afterwards" therefore means merging `preChange` with this,
 * not reading it directly. That is deliberate: these rows are written on every status change
 * across the whole system, and storing a second full copy of unchanged values would multiply
 * the table's size for data already sitting in the row above it.
 */
export const postChangeSchema = z
    .object({
    bookingStatus: z.string().nullable().optional(),
    chargesCreated: z.array(chargeCreatedSchema).optional(),
    chargesRestored: z.array(chargeRestoredSchema).optional(),
    /** Ids only: the full rows are already in `preChange.charges`. */
    chargesDeleted: z.array(entityId).optional(),
    /**
     * Total actually billed, summed from the persisted rows rather than the pre-insert
     * total. Charge amounts round to 2dp on the way in, so the raw computed total can
     * differ from what was billed - and the row has to reconcile with the charges, not
     * with the arithmetic that preceded them.
     *
     * This is where the total lives now that `EventLogs.amount` is gone.
     */
    appliedTotal: decimalString.optional(),
})
    .strict();
/**
 * Fields common to every event, matching the real columns on `EventLogs`.
 *
 * These are columns rather than metadata because they appear in WHERE clauses. SQL Server
 * has no native JSON type - JSON lives in NVARCHAR(MAX) and cannot be indexed without a
 * persisted computed column - so anything filtered on has to be promoted or it table-scans.
 *
 * Note this is not `.strict()`: feature schemas call `.extend(...).strict()` themselves, and
 * marking it strict here would reject their own added keys.
 */
export const eventLogBaseSchema = z.object({
    subjectType: z.enum(EVENT_SUBJECT_TYPE),
    subjectId: entityId,
    contextType: z.string().max(50).nullable().optional(),
    contextId: entityId.nullable().optional(),
    actorType: z.enum(ACTOR_TYPE),
    actorId: entityId.nullable().optional(),
    actorLabel: z.string().max(255).nullable().optional(),
    fromStatus: z.string().max(50).nullable().optional(),
    toStatus: z.string().max(50).nullable().optional(),
    summary: z.string().max(500).nullable().optional(),
    /** Bump when a metadata shape changes, so readers can branch instead of backfilling. */
    eventVersion: z.number().int().positive().optional(),
    /** Domain time, which is not always write time. Defaults to now at the service. */
    occurredAt: z.date().optional(),
    /**
     * Groups every event produced by one operation. Defaults to the Azure `invocationId`,
     * resolved by EventLogService rather than threaded through each caller.
     */
    correlationId: z.string().max(64).nullable().optional(),
    preChange: preChangeSchema.nullable().optional(),
    postChange: postChangeSchema.nullable().optional(),
});
//# sourceMappingURL=base.js.map